import { formatDate as cleaveFormatDate, formatCreditCard, formatGeneral } from 'cleave-zen';
import { format, isValid, parseISO } from 'date-fns';

/**
 * Mask configuration - consumers define their own mask format
 */

export type CreditCardConfig = {
  creditCard: true;
};
export type DateConfig = {
  date: true;
  delimiter: string;
  datePattern: string[];
};
export type GeneralConfig = {
  blocks: number[];
  numericOnly?: boolean;
  delimiters?: string[];
};

export type NumericOnlyConfig = {
  numericOnly: true;
};

export type MaskConfig = (CreditCardConfig | DateConfig | GeneralConfig | NumericOnlyConfig) & {
  prefix?: string;
};

/**
 * Converts datePattern and delimiter to date-fns format string
 */
const convertDatePatternToDateFnsFormat = (config: DateConfig): string => {
  return config.datePattern
    .map(pattern => {
      if (pattern === 'Y' || pattern === 'y') {
        return 'yyyy';
      }
      if (pattern === 'm') {
        return 'MM';
      }
      if (pattern === 'd') {
        return 'dd';
      }
      return pattern;
    })
    .join(config.delimiter);
};

const unitWidths = (config: DateConfig): number[] => config.datePattern.map(pattern => (pattern === 'Y' ? 4 : 2));

const unitMax = (pattern: string): number | null => {
  if (pattern === 'm') return 12;
  if (pattern === 'd') return 31;
  return null;
};

/** Largest digit that can open a unit: a `5` cannot start a day, so it can only mean `05`. */
const unitMaxFirst = (pattern: string): number | null => {
  if (pattern === 'm') return 1;
  if (pattern === 'd') return 3;
  return null;
};

/** One entry per unit of the pattern, so a missing or short unit still has a place to be edited. */
const toUnits = (value: string, config: DateConfig): string[] => {
  const parts = value.split(config.delimiter);

  return unitWidths(config).map((_, index) => (parts[index] ?? '').replace(/\D/g, ''));
};

/**
 * The units the value renders. The delimiter after the last one appears only once that unit is full,
 * which is when there is a next unit to type into — the same point cleave appends it.
 */
const visibleUnits = (units: string[], config: DateConfig): string[] => {
  const widths = unitWidths(config);
  const lastFilled = units.reduce((last, unit, index) => (unit === '' ? last : index), -1);
  const complete = lastFilled >= 0 && units[lastFilled].length === widths[lastFilled];

  return units.slice(0, Math.max(lastFilled + (complete ? 2 : 1), 1));
};

const fromUnits = (units: string[], config: DateConfig): string => visibleUnits(units, config).join(config.delimiter);

/** Where each character of the rendered value lives — `null` marks a delimiter. */
const toCells = (units: string[], config: DateConfig): ({ unit: number; offset: number } | null)[] => {
  const cells: ({ unit: number; offset: number } | null)[] = [];

  units.forEach((unit, index) => {
    for (let offset = 0; offset < unit.length; offset += 1) cells.push({ unit: index, offset });
    if (index < units.length - 1) for (let n = 0; n < config.delimiter.length; n += 1) cells.push(null);
  });

  return cells;
};

const caretOf = (units: string[], config: DateConfig, unit: number, offset: number): number => {
  let position = offset;

  for (let index = 0; index < unit; index += 1) position += units[index].length + config.delimiter.length;

  return position;
};

/** Which unit a caret position sits in. A position on a delimiter belongs to the unit after it. */
const locate = (units: string[], config: DateConfig, position: number): { unit: number; offset: number } => {
  let start = 0;

  for (let index = 0; index < units.length; index += 1) {
    const end = start + units[index].length;

    if (position <= end) return { unit: index, offset: position - start };

    start = end + config.delimiter.length;

    if (position < start) return { unit: index + 1, offset: 0 };
  }

  return { unit: units.length - 1, offset: units[units.length - 1].length };
};

/**
 * Trims a unit to its width, and caps it once it is full.
 *
 * Only a full unit is capped. A short one is mid-edit — the `7` left by deleting a digit out of
 * `17` is not an out-of-range day, and padding it to `07` is the re-pack this whole function exists
 * to avoid.
 */
const formatUnit = (unit: string, width: number, max: number | null): string => {
  const digits = unit.replace(/\D/g, '').slice(0, width);

  if (max === null || digits.length < width || Number(digits) <= max) return digits;

  return String(max);
};

type Cursor = { unit: number; offset: number };

/**
 * `caret` is where the browser left the caret, so it is the position *after* the edit.
 *
 * `isForward` separates the one pair that is otherwise identical: on a trailing delimiter, Backspace
 * and Delete produce the same `rawValue` and the same caret. It defaults to false so an edit with no
 * `inputType` behaves like Backspace, which is what the platforms that omit it can produce anyway.
 */
export type DateEditInput = { previousValue: string; rawValue: string; caret: number; isForward?: boolean };

/**
 * Writes one digit into `units`, mutating it, and returns where the caret goes next.
 *
 * Overtyping a full unit is what lets a complete date be retyped left to right; trimming its tail
 * instead would discard whichever digit the caret was not sitting on. Returns null when nothing
 * downstream has space.
 */
const writeDigit = (units: string[], config: DateConfig, digit: string, from: Cursor): Cursor | null => {
  const widths = unitWidths(config);
  let { unit, offset } = from;

  while (unit < units.length) {
    const width = widths[unit];
    const next = offset + 1 >= width ? { unit: unit + 1, offset: 0 } : { unit, offset: offset + 1 };
    const maxFirst = unitMaxFirst(config.datePattern[unit]);

    if (offset === 0 && maxFirst !== null && Number(digit) > maxFirst) {
      units[unit] = `0${digit}`;
      return { unit: unit + 1, offset: 0 };
    }

    if (units[unit].length < width) {
      units[unit] = `${units[unit].slice(0, offset)}${digit}${units[unit].slice(offset)}`.slice(0, width);
      return next;
    }

    if (offset < width) {
      units[unit] = `${units[unit].slice(0, offset)}${digit}${units[unit].slice(offset + 1)}`;
      return next;
    }

    unit += 1;
    offset = 0;
  }

  return null;
};

/**
 * Replays one edit against the value the element held before it.
 *
 * The browser has already spliced characters in and out, which loses the mask's structure — a
 * selection that spanned a delimiter takes the delimiter with it, and the leftover falls a unit
 * short. Reconstructing the edit as "these positions went, these digits arrived" puts every case
 * (a keystroke, a backspace, a range retyped, a paste) through the same placement rule, so digits
 * never migrate between units. Returns null when the edit is not one this can describe.
 */
export const applyDateEdit = (
  { previousValue, rawValue, caret, isForward = false }: DateEditInput,
  config?: MaskConfig,
): { value: string; caret: number } | null => {
  if (!config || !('date' in config) || config.date !== true) return null;

  const tail = rawValue.length - caret;
  const removedTo = previousValue.length - tail;

  if (tail < 0 || removedTo < 0 || rawValue.slice(caret) !== previousValue.slice(removedTo)) return null;

  let from = 0;
  while (from < caret && from < removedTo && previousValue[from] === rawValue[from]) from += 1;

  const inserted = rawValue.slice(from, caret);

  if (!/^\d*$/.test(inserted)) return null;

  const units = toUnits(previousValue, config);
  const cells = toCells(visibleUnits(units, config), config);
  const landing = locate(units, config, from);

  // Everything the edit covered leaves, except the delimiters — those are the mask's own, and
  // dropping one is what lets the units either side collapse into each other.
  const gone = cells.slice(from, removedTo).filter((cell): cell is Cursor => cell !== null);

  // A delimiter with nothing to its right is guarding the digit behind it. Restoring it and taking
  // nothing would leave the value unchanged, so the backspace would stall. `removedTo > from` keeps
  // that to edits that actually covered the delimiter, rather than ones that covered nothing.
  if (
    !isForward &&
    gone.length === 0 &&
    inserted === '' &&
    removedTo > from &&
    removedTo === previousValue.length &&
    from > 0
  ) {
    const guarded = locate(units, config, from - 1);

    if (units[guarded.unit].length > 0) {
      gone.push({ unit: guarded.unit, offset: Math.min(guarded.offset, units[guarded.unit].length - 1) });
    }
  }

  for (const { unit, offset } of [...gone].sort((a, b) => b.offset - a.offset)) {
    units[unit] = units[unit].slice(0, offset) + units[unit].slice(offset + 1);
  }

  // A forward delete over a delimiter took nothing, so step the caret past it — otherwise the next
  // press repeats the same no-op.
  let cursor: Cursor | null =
    isForward && gone.length === 0 && inserted === '' && removedTo > from
      ? { unit: Math.min(landing.unit + 1, units.length - 1), offset: 0 }
      : landing;

  for (const digit of inserted) {
    if (!cursor) break;
    cursor = writeDigit(units, config, digit, cursor);
  }

  const capped = units.map((unit, index) =>
    formatUnit(unit, unitWidths(config)[index], unitMax(config.datePattern[index])),
  );
  const settled = cursor ?? { unit: capped.length - 1, offset: capped[capped.length - 1].length };
  const value = fromUnits(capped, config);

  return {
    value,
    caret: Math.min(caretOf(capped, config, settled.unit, settled.offset), value.length),
  };
};

/**
 * Formats a date value using the provided date configuration.
 *
 * cleave re-packs the whole digit stream from the left, which destroys a unit edited in place:
 * `08/7/1982` comes back as `08/07/982`. A value that already carries every delimiter says where
 * its units start, so each one is trimmed on its own and nothing shifts.
 */
const formatDateValue = (value: string, config: DateConfig): string => {
  const isValidIsoDate = value.length === 10 && isValid(parseISO(value));

  const seeded = isValidIsoDate ? format(parseISO(value), convertDatePatternToDateFnsFormat(config)) : value;

  const widths = unitWidths(config);
  const split = seeded.split(config.delimiter);

  // Keep one empty unit past the last filled one — that is the delimiter the mask appends. The
  // rest are delimiters typed over it, which nobody has reached yet. An empty unit with digits
  // behind it is one the user deleted, and stays where it is.
  const lastFilled = split.reduce((last, unit, index) => (unit === '' ? last : index), -1);
  const units = split.slice(0, Math.max(lastFilled + 2, 1));

  // With every unit present the value says where its own units start, so each is trimmed on its
  // own and an edit inside one cannot shift the others. Any other shape — a delimiter added or
  // deleted — goes to cleave exactly as it always did.
  if (units.length === widths.length) {
    return units
      .map((unit, index) => formatUnit(unit, widths[index], unitMax(config.datePattern[index])))
      .join(config.delimiter);
  }

  return cleaveFormatDate(units.join(config.delimiter), {
    datePattern: config.datePattern as ('Y' | 'y' | 'm' | 'd')[],
    delimiter: config.delimiter,
  });
};

/**
 * Formatting function using cleave-zen internally
 */
export const formatValue = (value: string, config?: MaskConfig): string => {
  if (!config) return value;

  // Handle credit card formatting
  if ('creditCard' in config && config.creditCard === true) {
    return formatCreditCard(value);
  }

  // Handle date formatting
  if ('date' in config && config.date === true) {
    return formatDateValue(value, config);
  }

  // Handle numeric formatting with delimiters and blocks
  if ('blocks' in config && config.blocks) {
    return formatGeneral(value, {
      blocks: config.blocks,
      delimiters: config.delimiters,
      numericOnly: config.numericOnly,
    });
  }

  // Handle simple numeric-only formatting
  if ('numericOnly' in config && config.numericOnly) {
    return value.replace(/\D/g, '');
  }

  return value;
};
